import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, ImagePlus, Music, Loader2, Mic, Trash2 } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { socialApi, mediaUrl } from "@/api/social";
import VaultPickButton from "@/components/mist/VaultPickButton";

export default function PostComposerModal({ onClose, onPosted }) {
  const { user, token } = useSession();
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]); // {id, kind, name}
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const imgRef = useRef(null);
  const audRef = useRef(null);

  const pick = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (kind === "audio" && !/\.wav$/i.test(file.name) && !file.type.includes("wav")) {
      toast.error("Only .wav audio files are supported");
      return;
    }
    setUploading(true);
    try {
      const res = await socialApi.upload(file);
      setAttachments((a) => [...a, { id: res.id, kind: res.kind, name: file.name }]);
      toast.success(`${kind === "image" ? "Image" : "Audio"} attached`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const submit = async () => {
    if (!text.trim() && attachments.length === 0) {
      toast("Write something or attach media", { icon: "✏️" });
      return;
    }
    setPosting(true);
    try {
      const media = attachments.map((a) => ({ id: a.id, kind: a.kind }));
      const { post } = await socialApi.createPost(text.trim(), media);
      toast.success("Posted");
      onPosted?.(post);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="absolute inset-0 z-[60] bg-black/75 flex items-end justify-center" data-testid="post-composer-modal">
      <div className="w-full max-w-[412px] bg-cream border-t-4 border-charcoal rounded-t-3xl p-4 shadow-hard space-y-3 max-h-[92dvh] overflow-y-auto no-scrollbar">
        {/* header */}
        <div className="flex items-center gap-2.5 pb-2 border-b-2 border-charcoal/10">
          <div className="w-9 h-9 rounded-full border-2 border-charcoal overflow-hidden bg-gold grid place-items-center shrink-0">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="font-mono text-[10px] font-black text-charcoal">
                {(user?.name || "?").slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-lg text-charcoal leading-none truncate">{user?.name}</p>
            <span className="font-mono text-[9px] text-charcoal/40">New post</span>
          </div>
          <button onClick={onClose} data-testid="composer-close-btn" aria-label="Close" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-charcoal bg-white shadow-[1.5px_1.5px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <X size={15} strokeWidth={2.6} className="text-charcoal" />
          </button>
        </div>

        {/* text */}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder="What's on your mind?"
          data-testid="composer-text"
          className="w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-sm text-charcoal outline-none focus:ring-2 focus:ring-gold resize-none shadow-[2px_2px_0px_#1A1A1A]"
        />

        {/* attachments preview */}
        {attachments.length > 0 && (
          <div className="space-y-2" data-testid="composer-attachments">
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 bg-white border-2 border-charcoal rounded-xl p-2 shadow-[2px_2px_0px_#1A1A1A]">
                {a.kind === "image" ? (
                  <img src={mediaUrl(a.id, token)} alt={a.name} className="w-14 h-14 rounded-lg object-cover border-2 border-charcoal" />
                ) : (
                  <audio src={mediaUrl(a.id, token)} controls className="flex-1 h-9" data-testid="composer-audio-preview" />
                )}
                <span className="flex-1 min-w-0 font-mono text-[10px] text-charcoal/60 truncate">{a.name}</span>
                <button onClick={() => removeAt(a.id)} data-testid={`composer-remove-${a.id}`} className="w-7 h-7 grid place-items-center rounded-lg border-2 border-charcoal bg-red-50 text-red-600">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* attach controls */}
        <div className="flex items-center gap-2">
          <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => pick(e, "image")} data-testid="composer-image-input" />
          <input ref={audRef} type="file" accept=".wav,audio/wav" hidden onChange={(e) => pick(e, "audio")} data-testid="composer-audio-input" />
          <button onClick={() => imgRef.current?.click()} disabled={uploading} data-testid="composer-add-image-btn" className="flex-1 flex items-center justify-center gap-1.5 bg-white border-2 border-charcoal rounded-lg py-2 font-mono text-[10px] font-black uppercase tracking-wider shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <ImagePlus size={14} /> Image
          </button>
          <VaultPickButton onFile={(f) => pick({ target: { files: [f], value: "" } }, "image")} bucket="images" testid="composer-image-vault" disabled={uploading} label="Vault"
            className="flex-1 flex items-center justify-center gap-1.5 bg-charcoal border-2 border-charcoal text-[#00F5D4] rounded-lg py-2 font-mono text-[10px] font-black uppercase tracking-wider shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none" />
          <button onClick={() => audRef.current?.click()} disabled={uploading} data-testid="composer-add-audio-btn" className="flex-1 flex items-center justify-center gap-1.5 bg-white border-2 border-charcoal rounded-lg py-2 font-mono text-[10px] font-black uppercase tracking-wider shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <Music size={14} /> Audio
          </button>
          {/* Placeholder: in-browser mic recording (framework only) */}
          <button onClick={() => toast("Voice recording — coming soon", { icon: "🎙️" })} data-testid="composer-record-btn" className="w-10 flex items-center justify-center bg-charcoal/5 border-2 border-dashed border-charcoal/30 text-charcoal/40 rounded-lg py-2">
            <Mic size={14} />
          </button>
        </div>

        {/* submit */}
        <button
          onClick={submit}
          disabled={posting || uploading}
          data-testid="composer-post-btn"
          className="w-full bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 rounded-xl border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-[transform,box-shadow] duration-150 disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {(posting || uploading) ? <Loader2 size={15} className="animate-spin" /> : null}
          {uploading ? "Uploading…" : posting ? "Posting…" : "Post"}
        </button>
      </div>
    </div>
  );
}
